// Phase 26A · Favorites hook.
// Single source of truth for the current user's favorites list.
// Instances stay in sync via a lightweight event bus (`favorites:changed`).
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const EVT = "favorites:changed";

async function fetchFavorites() {
  const { data } = await api.get("/user-favorites");
  return data.items || [];
}

function broadcast() {
  window.dispatchEvent(new CustomEvent(EVT));
}

export function useFavorites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setItems(await fetchFavorites());
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener(EVT, onChange);
    return () => window.removeEventListener(EVT, onChange);
  }, [load]);

  const isFav = useCallback(
    (key) => items.some((f) => f.item_key === key),
    [items]
  );

  const add = useCallback(async ({ item_type, item_key, item_label, item_route }) => {
    try {
      await api.post("/user-favorites", { item_type, item_key, item_label, item_route });
      broadcast();
    } catch (e) {
      // 409 = already favorited; refresh silently.
      if (e?.response?.status !== 409) throw e;
      broadcast();
    }
  }, []);

  const remove = useCallback(async (item_key) => {
    try {
      await api.delete(`/user-favorites/${encodeURIComponent(item_key)}`);
    } catch (e) {
      if (e?.response?.status !== 404) throw e;
    }
    broadcast();
  }, []);

  const toggle = useCallback(
    async (payload) => {
      if (isFav(payload.item_key)) return remove(payload.item_key);
      return add(payload);
    },
    [isFav, add, remove]
  );

  const reorder = useCallback(async (ordered_keys) => {
    await api.patch("/user-favorites/reorder", { ordered_keys });
    broadcast();
  }, []);

  return { items, loading, error, isFav, toggle, add, remove, reorder, reload: load };
}
